import { createApp } from '@backstage/frontend-defaults';
import { createFrontendModule } from '@backstage/frontend-plugin-api';
import { SignInPageBlueprint } from '@backstage/plugin-app-react';
import { convertLegacyAppOptions, convertLegacyAppRoot, } from '@backstage/core-compat-api';
import { AppRouter, FlatRoutes } from "@backstage/core-app-api";
import { githubAuthApiRef } from "@backstage/core-plugin-api";
import {
  SignInPage,
} from "@backstage/core-components";
import { SignInProviderConfig } from "@backstage/core-components";
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from "@backstage/plugin-catalog";
import { UserListFilterKind } from "@backstage/plugin-catalog-react";
import {
  scaffolderPlugin,
} from "@backstage/plugin-scaffolder";
import orgPlugin from "@backstage/plugin-org/alpha";
import { SearchPage } from "@backstage/plugin-search";
import githubActionsPlugin from '@backstage-community/plugin-github-actions/alpha';
import githubInsightsPlugin from '@roadiehq/backstage-plugin-github-insights/alpha';
import techDocsPlugin from "@backstage/plugin-techdocs/alpha"
import CatalogGraphPage from "@backstage/plugin-catalog-graph/alpha"
import NotificationsPage from '@backstage/plugin-notifications/alpha';
import UserSettingsPage from "@backstage/plugin-user-settings/alpha";
import apiDocsPlugin from "@backstage/plugin-api-docs/alpha";
import { Route } from "react-router-dom";
import { apiDocsConfigApi, scmAuthApi, scmIntegrationsApi } from "./apis";
import { Root } from "./components/Root";
import { entityPage } from "./components/catalog/EntityPage";
import { searchPage } from "./components/search/SearchPage";
import * as plugins from "./plugins";
import { oktaAuthApiRef } from "@backstage/core-plugin-api";
import { HomepageCompositionRoot } from "@backstage/plugin-home";
import { HomePage } from "./components/home/HomePage";
import ExplorePage from "@backstage-community/plugin-explore/alpha";
import { techDocsMermaidAddonModule } from "backstage-plugin-techdocs-addon-mermaid";
import { ReadAwsDataPage } from "plugin-read-aws-data";
import { ComplianceUiPage } from "@internal/plugin-compliance-ui";
import { DevopsMetricsPage } from "@internal/plugin-devops-metrics";
import { DevToolsPage } from "@backstage/plugin-devtools";
import { customDevToolsPage } from "./components/devtools/CustomDevToolsPage";
import CatalogUnprocessedEntitiesPage from "@backstage/plugin-catalog-unprocessed-entities/alpha";
import { FeatureDeployPage, RegionalDeployPage } from "@internal/plugin-regional-deploy";
import authPlugin from '@backstage/plugin-auth';
import { ContainerScansPage } from '@internal/backstage-plugin-container-scans';

const providerOkta: SignInProviderConfig = {
  id: "okta-auth-provider",
  title: "Okta",
  message: "If you don't have a GitHub account, sign in using Okta",
  apiRef: oktaAuthApiRef,
};

const providerGitHub: SignInProviderConfig = {
  id: "github-auth-provider",
  title: "Github",
  message: "Sign in using GitHub",
  apiRef: githubAuthApiRef,
};

const convertedOptionsModule = convertLegacyAppOptions({
    /* legacy options such as apis, icons, plugins, components, themes and featureFlags */
    plugins: Object.values(plugins),
});

const signInPage = SignInPageBlueprint.make({
    params: {
        loader: async () => props =>
            (
                <SignInPage
                    {...props}
                    auto
                    providers={[providerGitHub, providerOkta]}
                />
            ),
    },
});

const filterAll: UserListFilterKind = "all";

const routes = (
    <FlatRoutes>
        <Route path="/" element={<HomepageCompositionRoot />}>
            <HomePage />
        </Route>
        <Route
            path="/catalog"
            element={<CatalogIndexPage initiallySelectedFilter={filterAll} />}
        />
        <Route path="/catalog/:namespace/:kind/:name" element={<CatalogEntityPage />}>
            {entityPage}
        </Route>
        <Route path="/search" element={<SearchPage />}>
            {searchPage}
        </Route>
        <Route path="/read-aws-data" element={<ReadAwsDataPage />} />
        <Route path="/compliance-ui" element={<ComplianceUiPage />} />
        <Route path="/devops-metrics" element={<DevopsMetricsPage />} />
        <Route path="/devtools" element={<DevToolsPage />}>
            {customDevToolsPage}
        </Route>
        <Route path="/feature-deploy" element={<FeatureDeployPage />} />
        <Route path="/regional-deploy" element={<RegionalDeployPage />} />
        <Route path="/container-scans" element={<ContainerScansPage />} />
    </FlatRoutes>
);

const convertedRootFeatures = convertLegacyAppRoot(
    <>
        <AppRouter>
            <Root>{routes}</Root>
        </AppRouter>
    </>,
);

const app = createApp({
    features: [
        convertedOptionsModule,
        ...convertedRootFeatures,
        createFrontendModule({
            pluginId: 'app',
            extensions: [scmIntegrationsApi, scmAuthApi, signInPage],
        }),
        // Override the api-docs plugin's built-in `config` API (api:api-docs/config)
        // with our Scalar-enabled renderer. Attaching to pluginId 'api-docs' makes
        // this replace the default rather than conflict with it.
        createFrontendModule({
            pluginId: 'api-docs',
            extensions: [apiDocsConfigApi],
        }),
        authPlugin,
        ExplorePage,
        apiDocsPlugin,
        UserSettingsPage,
        CatalogGraphPage,
        NotificationsPage,
        CatalogUnprocessedEntitiesPage,
        techDocsPlugin,
        techDocsMermaidAddonModule,
        githubActionsPlugin,
        githubInsightsPlugin,
    ],
    bindRoutes({ bind }) {
        bind(catalogPlugin.externalRoutes, {
            createComponent: scaffolderPlugin.routes.root,
            viewTechDoc: techDocsPlugin.routes.docRoot,
            createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
        });
        bind(orgPlugin.externalRoutes, {
            catalogIndex: catalogPlugin.routes.catalogIndex,
        });
    }
});

export default app.createRoot();
